import { history } from 'app';
import { Page } from 'new/components/Page';
import { LocaleContext } from 'new/i18n/LocaleContext';
import findRoute from 'new/routing/routes';
import FullPageTemplate from 'new/templates/FullPageTemplate';
import { mobileAndTabletCheck } from 'new/utils/system';
import { useContext, useEffect, useMemo } from 'react';
import { WithRouterProps } from 'react-router';
import OfficeHeader from '../components/OfficeHeader';
import AssociationBanner from '../components/landingPage/AssociationBanner';
import FAQ from '../components/landingPage/FAQ';
import FamilyDiscounts from '../components/landingPage/FamilyDiscounts';
import Footer from '../components/landingPage/Footer';
import HeroSection from '../components/landingPage/HeroSection';
import JoinMiddleSection from '../components/landingPage/JoinMiddleSection';
import JoinSection from '../components/landingPage/JoinSection';
import NoDentistFound from '../components/landingPage/NoDentistFound';
import Plans, { PlansProps } from '../components/landingPage/Plans';
import PlansVision, {
  PlansVisionProps,
} from '../components/landingPage/PlansVision';
import {
  useExternalPatientsStore,
  useOfficeInfoNoAuth,
  useGetNearbyZipcode,
  useSendInviteId,
} from '../hooks';
import { LandingPageCommonProps } from '../types';
import ClassicTitle from '../components/landingPage/ClassicTitle';
import ClassicPlans from '../components/landingPage/ClassicPlans';
import { Box, Stack, styled } from '@mui/material';
import LanguageToggle from '../components/LanguageToggle';
import classicBg from 'new/assets/landing_page_bg.svg';
import { TENANT } from 'new/tenants';
import { TenantStruct } from 'new/types/util';
import PlanTypeSelectorModal from '../components/landingPage/PlanTypeSelectorModal';
import Cookies from 'universal-cookie';
import { PlanType } from 'new/modules/InternalPatientSignup/types';
import CommunityBanner from '../components/landingPage/CommunityConnectBanner';
import { Plan } from 'new/modules/MembershipPlansManage/types';
const cookies = new Cookies();

const useSanitizeDentistInfoId = (dentistInfoId: string) => {
  useEffect(() => {
    if (
      dentistInfoId.includes(' ') ||
      dentistInfoId.includes(')') ||
      dentistInfoId.includes('.') ||
      dentistInfoId.includes('%C2%A0')
    ) {
      window.location.href =
        `/accounts/signup/my-${TENANT.professionalName}/` +
        dentistInfoId
          .replace(' ', '')
          .replace(')', '')
          .replace('.', '')
          .replace('%C2%A0', '');
    }
  }, []);
};

const ClassicWrapper = styled(Box)(() => ({
  backgroundColor: '#ffffff',
  backgroundImage: `url(${classicBg})`,
  backgroundAttachment: 'fixed',
  backgroundSize: 'cover',
  backgroundPositionY: '200px',
  height: '100vh',
  overflowY: 'auto',
}));

const plansComponents: TenantStruct<React.FC<PlansProps | PlansVisionProps>> = {
  dhq: Plans,
  vhq: PlansVision,
};

const PlansComponent = plansComponents[TENANT.id];

const SignupLandingPage: React.FC<WithRouterProps> = ({
  location: {
    query: { signuponly, esp, groupInfo, token, inviteId, insured },
  },
  params: { dentistInfoId },
}) => {
  const { setLocale } = useContext(LocaleContext);
  const {
    data: officeData,
    isLoading,
    isError,
  } = useOfficeInfoNoAuth(dentistInfoId, groupInfo, token);

  const { data: nearbyZipcodes } = useGetNearbyZipcode(dentistInfoId);

  useSanitizeDentistInfoId(dentistInfoId);
  const { planType, setPlanType } = useExternalPatientsStore();
  const { mutate: sendInviteId } = useSendInviteId();

  useEffect(() => {
    if (esp) setLocale('es-US');

    if (insured) {
      let newPlanType: PlanType | undefined = undefined;
      if (insured === 'true') newPlanType = 'insured';
      if (insured === 'false') newPlanType = 'uninsured';
      if (newPlanType) setPlanType?.(newPlanType);
    }

    if (signuponly) {
      history.push(
        `${findRoute('ExternalPatientSignup').path}?officeId=${dentistInfoId}`,
      );
    }
    if (
      inviteId &&
      inviteId != undefined &&
      inviteId != null &&
      inviteId != 'null' &&
      !cookies.get('inviteId')
    ) {
      sendInviteId({ inviteId });
      cookies.set('inviteId', inviteId, { path: '/' });
    }
  }, [esp, signuponly, dentistInfoId, inviteId, insured]);

  const isMobileView = useMemo(() => mobileAndTabletCheck(), []);

  if (isError) return <NoDentistFound />;

  const isNewTheme =
    officeData?.data.externalSignup != null &&
    officeData?.data.externalSignup.newVersion != 0;

  const activeMemberships = officeData?.data.memberships.filter(
    (mem) =>
      mem.viewStatus === 'active' &&
      ((mem.insured && planType === 'insured') ||
        (mem.uninsured && planType === 'uninsured') ||
        planType === undefined),
  );

  const customYearlyMemberships: Plan[] = [];
  activeMemberships?.map((m) => {
    if (
      (m.type == 'custom_year' || m.type == 'year') &&
      // m.active == true &&
      m.showOnEnrollment == true &&
      m.monthlyId
    )
      customYearlyMemberships[m.monthlyId] = m;
  });

  const standardMemberships = activeMemberships?.filter(
    (m) =>
      (m.type == 'month' || m.type == 'custom') &&
      (m.showOnEnrollment == true ||
        (m.showOnEnrollment == false &&
          Number(m.price) == 0 &&
          customYearlyMemberships[m.id] != undefined &&
          customYearlyMemberships[m.id].showOnEnrollment == true) ||
        (m.showOnEnrollment == false &&
          customYearlyMemberships[m.id] != undefined &&
          customYearlyMemberships[m.id].showOnEnrollment == true)),
  );

  const hasMemberships =
    (standardMemberships != undefined && standardMemberships?.length > 0) ||
    false;

  const hasSpanishPlans = standardMemberships?.some(
    (membership) => membership.spanishDescription,
  );

  const commonProps: LandingPageCommonProps = {
    colorOneHex: officeData?.data.externalSignup?.colorOneHex ?? '#fbfbfb',
    colorTwoHex: officeData?.data.externalSignup?.colorTwoHex ?? '#00A1DF',
    colorOne: officeData?.data.externalSignup?.colorOne ?? 'gray',
    colorTwo: officeData?.data.externalSignup?.colorTwo ?? 'gray',
    nearbyZipcodes: nearbyZipcodes ?? '',
    cityState: officeData?.data.city + ', ' + officeData?.data.state,
    bcToken: token,
    groupInfo: groupInfo,
  };

  return (
    <Page isLoading={isLoading} data={officeData} hasFullPageLayout>
      {(officeData) => (
        <FullPageTemplate>
          {isNewTheme ? (
            <>
              <OfficeHeader
                officeData={officeData}
                isMobileView={isMobileView}
                showJoinButton={!officeData.data.daoi.disableEnrollment}
                landingProps={commonProps}
                hasSpanishPlans={hasSpanishPlans}
                colorTwoHex={
                  officeData?.data.externalSignup?.colorTwoHex ?? '#00A1DF'
                }
                groupCode={
                  officeData?.data.smBusinessInfo?.smbizBusinessInvites
                    ?.groupCode
                }
              />
              <HeroSection
                officeData={officeData}
                {...commonProps}
                showJoinButton={!officeData.data.daoi.disableEnrollment}
              />
              {officeData?.data.externalSignup?.showAssociationBanner && (
                <AssociationBanner
                  externalSignup={officeData.data.externalSignup}
                />
              )}
              <JoinSection
                officeData={officeData}
                {...commonProps}
                showJoinButton={
                  !officeData.data.daoi.disableEnrollment &&
                  isMobileView &&
                  hasMemberships
                }
                smBusinessInfo={
                  groupInfo != undefined
                    ? officeData?.data.smBusinessInfo
                    : undefined
                }
              />
              {activeMemberships?.length && (
                <PlansComponent
                  {...commonProps}
                  memberships={activeMemberships}
                  smBusinessInfo={officeData?.data.smBusinessInfo}
                />
              )}
              <FamilyDiscounts
                dentistInfo={officeData.data.dentistInfo}
                isNewTheme={true}
              />
              <JoinMiddleSection
                {...commonProps}
                dentistInfoId={officeData.data.dentistInfo.id}
                disableEnrollment={
                  officeData.data.daoi.disableEnrollment || !hasMemberships
                }
                gpp={officeData.data.gpp}
                isNewTheme={isNewTheme}
                groupCode={
                  officeData?.data.smBusinessInfo?.smbizBusinessInvites
                    ?.groupCode
                }
                phone={officeData.data.phone}
              />
              <FAQ {...commonProps} />
              <Footer isNewTheme={isNewTheme} />
            </>
          ) : (
            <ClassicWrapper>
              {hasSpanishPlans && (
                <Stack direction="row" justifyContent="flex-end" px={2}>
                  <LanguageToggle />
                </Stack>
              )}
              <ClassicTitle dentistInfo={officeData.data.dentistInfo} />
              {groupInfo != undefined && (
                <CommunityBanner
                  smBusinessInfo={officeData?.data.smBusinessInfo}
                />
              )}
              {activeMemberships?.length && (
                <ClassicPlans
                  memberships={activeMemberships}
                  smBusinessInfo={officeData?.data.smBusinessInfo}
                />
              )}
              {groupInfo == undefined && (
                <FamilyDiscounts
                  dentistInfo={officeData.data.dentistInfo}
                  isNewTheme={isNewTheme}
                />
              )}
              <JoinMiddleSection
                {...commonProps}
                dentistInfoId={officeData.data.dentistInfo.id}
                disableEnrollment={
                  officeData.data.daoi.disableEnrollment || !hasMemberships
                }
                gpp={officeData.data.gpp}
                isNewTheme={isNewTheme}
                groupCode={
                  officeData?.data.smBusinessInfo?.smbizBusinessInvites
                    ?.groupCode
                }
                phone={officeData.data.phone}
              />
              <Footer isNewTheme={isNewTheme} />
            </ClassicWrapper>
          )}
          <PlanTypeSelectorModal hasInsured={officeData?.data.hasInsured} />
        </FullPageTemplate>
      )}
    </Page>
  );
};

export default SignupLandingPage;
